import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import queryString from 'query-string';
import { BackTo } from '@deeptrue-frontend/ui';
import { useFormatMessage } from 'components/IntlProvider';
import { Dashboard } from 'components/Layout';
import api from 'api/api';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { OrgnizationInvitationsType } from 'store/modules/organizations/organizations';
import RouterProvider from 'components/RouterProvider/RouterProvider';
import Invitations from 'components/Invitations/Invitations';
import EmptyPlaceholder from 'components/EmptyPlaceholder/EmptyPlaceholder';

const { ROUTES, Redirect } = RouterProvider;
const { connect, selectors, actions } = StoreProvider;

interface OrganizationUsersInvitationsProps
  extends Omit<RouteComponentProps, 'match'> {
  invitations: OrgnizationInvitationsType;
  setOrganizationsInvitations: (payload: any) => void;
  match: {
    params: {
      codename: string;
    };
  };
}

const OrganizationUsersInvitations = ({
  invitations,
  setOrganizationsInvitations,
  match: { params },
  location,
  history,
}: OrganizationUsersInvitationsProps) => {
  const t = useFormatMessage();
  const [isLoaded, setIsLoaded] = useState(false);
  const parsedSearch = queryString.parse(location.search);

  const refreshInvitationList = () =>
    api.resources
      .getOrganizationInvitations({
        organization_name: params.codename,
        params: parsedSearch as { limit: string; offset: string },
      })
      .then(({ data }) => {
        setOrganizationsInvitations(data);
      })
      .finally(() => {
        setIsLoaded(true);
      });

  useEffect(() => {
    if (params.codename) {
      setIsLoaded(false);
      refreshInvitationList();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const getPageNumber = () => {
    if (parsedSearch.offset && !Array.isArray(parsedSearch.offset)) {
      return parseInt(parsedSearch.offset, 10) / 20;
    }

    return 0;
  };

  if (!params.codename) {
    return <Redirect to={ROUTES.ADMIN.ORGANIZATIONS.LIST} />;
  }

  return (
    <Dashboard
      title={`Organization ${params.codename} users invitations`}
      location={location}
    >
      <BackTo onClick={() => history.push('/admin/organizations')}>
        {t('backTo.organizations', 'Back to Organizations')}
      </BackTo>
      <EmptyPlaceholder
        isLoaded={isLoaded}
        hasValues={invitations.list.length > 0}
        withLoader
      >
        <Invitations
          list={invitations.list}
          count={invitations.count}
          currentPage={getPageNumber()}
          refresh={refreshInvitationList}
        />
      </EmptyPlaceholder>
    </Dashboard>
  );
};

export default connect(
  (state) => ({
    invitations: selectors.organizationsSelectors.invitations(state),
  }),
  {
    setOrganizationsInvitations:
      actions.organizationsActions.setOrganizationsInvitations,
  }
)(OrganizationUsersInvitations);
